/* Property-derived metrics — diagnostic, computed from real snapshots/financials.
   Inlined helper used by the Property Detail tabs (no shared-file edits). */
import type { Dataset, Property, FinancialPeriod, PropertyHealth } from '@/types/entities'
import { latest, sumGroup } from '@/lib/selectors'

export interface DelinquencyRead {
  /** Bad-debt as % of gross rental income for the latest period, if financials exist. */
  pct?: number
  /** Absolute bad-debt dollars (positive). */
  amount?: number
  /** Where the figure came from. */
  source?: 'financials'
}

/** Pull a delinquency read from the property's financial period bad-debt line. */
export function delinquencyRead(fp?: FinancialPeriod): DelinquencyRead {
  if (!fp) return {}
  const badDebt = fp.lines.find((l) => l.label.toLowerCase().includes('bad debt'))
  if (!badDebt) return {}
  const gross = fp.lines.find((l) => /gross rental/i.test(l.label))?.actual
    ?? sumGroup(fp, 'revenue', 'actual')
  const amount = Math.abs(badDebt.actual)
  const pct = gross ? (amount / Math.abs(gross)) * 100 : undefined
  return { pct, amount, source: 'financials' }
}

/** Trend direction of a snapshot series: latest vs first. */
export function trendDelta(snaps?: { date: string; value: number }[]): number | undefined {
  if (!snaps || snaps.length < 2) return undefined
  const sorted = [...snaps].sort((a, b) => (a.date < b.date ? -1 : 1))
  return sorted[sorted.length - 1].value - sorted[0].value
}

/** Lease velocity proxy: net occupied-sqft change over the snapshot window. */
export function netAbsorption(p: Property): number | undefined {
  return trendDelta(p.occupiedSqFt)
}

export interface DiagnosticVerdict {
  verdict: PropertyHealth
  reasons: string[]
}

/** Light, transparent Healthy/Watch/At-Risk from occupancy + delinquency + revenue trend.
 *  Defers to the seed `health` when present (that's the asset team's call), but always
 *  surfaces the computed reasons so the page reads as a diagnostic, not a black box. */
export function diagnose(p: Property, fp?: FinancialPeriod): DiagnosticVerdict {
  const occ = latest(p.occupancyPct)
  const del = delinquencyRead(fp)
  const revTrend = trendDelta(p.revenue)
  const reasons: string[] = []
  let score = 0 // negative = worse

  if (occ != null) {
    if (occ >= 90) { reasons.push(`Occupancy ${occ.toFixed(1)}% — at or above stabilized target`) }
    else if (occ >= 85) { score -= 1; reasons.push(`Occupancy ${occ.toFixed(1)}% — slightly below the 90% target`) }
    else { score -= 2; reasons.push(`Occupancy ${occ.toFixed(1)}% — materially below target`) }
  }
  if (del.pct != null) {
    if (del.pct >= 20) { score -= 2; reasons.push(`Delinquency ${del.pct.toFixed(1)}% of in-place rent — elevated`) }
    else if (del.pct >= 8) { score -= 1; reasons.push(`Delinquency ${del.pct.toFixed(1)}% of in-place rent — watch`) }
    else { reasons.push(`Delinquency ${del.pct.toFixed(1)}% of in-place rent — in range`) }
  }
  if (revTrend != null) {
    if (revTrend < 0) { score -= 1; reasons.push('Revenue trending down across the snapshot window') }
    else { reasons.push('Revenue trending up across the snapshot window') }
  }

  const verdict: PropertyHealth = score <= -3 ? 'At-Risk' : score <= -1 ? 'Watch' : 'Healthy'
  return { verdict: p.health ?? verdict, reasons }
}

/** First financial period attached to a property (if any). */
export function propertyFinancialPeriod(d: Dataset, p: Property): FinancialPeriod | undefined {
  const id = p.financialPeriodIds?.[0]
  if (id) return d.financialPeriods.find((f) => f.id === id)
  return d.financialPeriods.find((f) => f.propertyId === p.id)
}
